import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertTriangle } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';

interface EscalateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (notes: string) => Promise<void>;
  action: 'escalate' | 'cancel';
}

export const EscalateModal = ({ isOpen, onClose, onSubmit, action }: EscalateModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useOnClickOutside(modalRef, onClose);

  if (!isOpen) return null;

  const isEscalate = action === 'escalate';
  const title = isEscalate ? 'Escalate Appeal' : 'Cancel Appeal';
  const description = isEscalate
    ? 'This will move the appeal to escalated status for senior review.'
    : 'This will permanently cancel this appeal.';
  const buttonText = isEscalate ? 'Escalate' : 'Cancel Appeal';

  const handleSubmit = async () => {
    if (!notes.trim()) {
      setError('Please provide a reason.');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(notes.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} appeal`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return createPortal(
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-lg"
      >
        <button
          onClick={onClose}
          className="modal-close"
          title="Close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="modal-header">
          <div className="min-w-0">
            <h2 className="modal-title flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${isEscalate ? 'text-amber-400' : 'text-red-400'}`} />
              {title}
            </h2>
            <p className="modal-subtitle">{description}</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-glass-400 mb-1.5">
              Reason <span className="text-red-400">*</span>
            </label>
            <textarea
              className="glass-input w-full min-h-[100px] resize-y"
              placeholder={isEscalate ? 'Explain why this needs senior review...' : 'Explain why this appeal is being cancelled...'}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              <span>{error}</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="glass-button-secondary">
            Go Back
          </button>
          <button
            onClick={handleSubmit}
            className={isEscalate ? 'btn-primary' : 'btn-danger'}
            disabled={isSubmitting || !notes.trim()}
          >
            {isSubmitting ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              buttonText
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
